<template>
  <div class="ur-sel-grid-panel">
    <!-- 手機：返回試算表（回到 LeftPanel）-->
    <button
      v-if="isMobile"
      class="ur-sel-grid-back"
      type="button"
      @click="$emit('back')"
    >
      <span class="ur-sel-grid-back-arrow">&#8592;</span>{{ $t('URBANRENEWAL_SELECT.PANEL.MOBILE_BACK') }}
    </button>

    <!-- 頂部列：左分頁（房屋/車位）＋ 右側選配期間/最後更新時間（設計稿位置） -->
    <div class="ur-sel-grid-topbar">
      <div class="ur-sel-grid-tabs">
        <button
          class="ur-sel-grid-tab"
          :class="{ 'is-active': activeTab === 'housing' }"
          @click="activeTab = 'housing'"
        >
          {{ $t('URBANRENEWAL_SELECT.PANEL.HOUSE') }}
        </button>
        <button
          class="ur-sel-grid-tab"
          :class="{ 'is-active': activeTab === 'parking' }"
          @click="activeTab = 'parking'"
        >
          {{ $t('URBANRENEWAL_SELECT.PANEL.PARKING') }}
        </button>
      </div>

      <div class="ur-sel-grid-topinfo">
        <span v-if="statsUpdated" class="ur-sel-grid-updated">
          {{ $t('URBANRENEWAL_SELECT.STATS_UPDATED') }}：{{ statsUpdatedText }}
        </span>
      </div>
    </div>

    <!-- 110% 超選警示列 -->
    <div v-if="isOverSelect" class="ur-sel-grid-warn">
      {{ $t('URBANRENEWAL_SELECT.GRID.OVER_LIMIT_WARN') }}
    </div>

    <!-- 空狀態 -->
    <div v-if="gridRows.length === 0" class="ur-sel-grid-empty">
      {{ $t('URBANRENEWAL_SELECT.GRID.NO_DATA') }}
    </div>

    <!-- 全樓層一次呈現、可捲動的格子矩陣（上色 + 點擊開單元彈窗：FE-P05） -->
    <div v-else class="ur-sel-grid-wrap">
      <table class="ur-sel-grid-table">
        <tbody>
          <tr v-for="row in gridRows" :key="row.floor">
            <td class="ur-sel-grid-floor">{{ row.floor }}</td>
            <td
              v-for="cell in row.cells"
              :key="cell.unit_code"
              class="ur-sel-grid-cell"
              :class="cellClass(cell)"
              @click.stop="openUnit(cell, $event)"
            >
              <div class="ur-sel-grid-code">{{ cell.unit }}</div>
              <div class="ur-sel-grid-count">
                {{ effectiveCount(cell) }} {{ $t('URBANRENEWAL_SELECT.GRID.UNIT_GROUP') }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 圖例：無/1/多（三態）＋ 非優先橙（僅房屋分頁） -->
    <div class="ur-sel-grid-legend">
      <div class="ur-sel-grid-legend-item">
        <span class="ur-sel-grid-legend-dot" style="background:#FFFFFF;border:1px solid #888888;"></span>
        {{ $t('URBANRENEWAL_SELECT.GRID.LGD_NONE') }}
      </div>
      <div class="ur-sel-grid-legend-item">
        <span class="ur-sel-grid-legend-dot" style="background:#bde3f5;border:1px solid #1A6894;"></span>
        {{ $t('URBANRENEWAL_SELECT.GRID.LGD_ONE') }}
      </div>
      <div class="ur-sel-grid-legend-item">
        <span class="ur-sel-grid-legend-dot" style="background:#f4bec2;border:1px solid #922B21;"></span>
        {{ $t('URBANRENEWAL_SELECT.GRID.LGD_MULTI') }}
      </div>
      <!-- 非優先區橘色圖例暫時註解（客戶：標記會被人針對性挑選）；點擊警告仍保留
      <div v-if="activeTab === 'housing'" class="ur-sel-grid-legend-item">
        <span class="ur-sel-grid-legend-dot" style="background:#ffecd4;border:1px solid #c8791a;"></span>
        {{ $t('URBANRENEWAL_SELECT.GRID.LGD_NONPRIORITY') }}
      </div>
      -->
    </div>

    <!-- 單元細節 popover（點擊格子貼齊彈出；設計稿 UnitPopover）。key 讓切換格子時重掛、重新定位 -->
    <UnitDialog
      v-if="dialogUnit"
      :key="dialogType + '-' + dialogUnit.id"
      :unit="dialogUnit"
      :type="dialogType"
      :count="effectiveCount(dialogUnit)"
      :already-added="isOwnSelected(dialogUnit)"
      :anchor-rect="anchorRect"
      @add="addFromDialog"
      @remove="removeFromDialog"
      @close="closeUnit"
    />

    <!-- 非優先選配區確認/重選（FE-P08） -->
    <NonPriorityDialog
      v-if="nonPriorityVisible"
      @confirm="confirmNonPriority"
      @reselect="cancelNonPriority"
      @close="cancelNonPriority"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import UnitDialog from './UnitDialog.vue';
import NonPriorityDialog from './NonPriorityDialog.vue';

export default {
  name: 'UrbanRenewalSelectRightPanel',

  components: {
    UnitDialog,
    NonPriorityDialog,
  },

  props: {
    isMobile: { type: Boolean, default: false },
  },

  data() {
    return {
      activeTab: 'housing',
      dialogUnit: null,
      dialogType: 'housing',
      anchorRect: null,
      pendingUnit: null,
      nonPriorityVisible: false,
    };
  },

  computed: {
    ...mapState('urbanRenewalSelect', ['grid', 'statsUpdated', 'selectedHousing', 'selectedParking']),
    ...mapGetters('urbanRenewalSelect', ['isOverSelect']),

    // 顯示格式：ISO「2026-07-16T10:30:00.123456」→「2026-07-16 10:30:00」（T→空格、去小數秒/時區）
    statsUpdatedText() {
      if (!this.statsUpdated) return '';
      return String(this.statsUpdated)
        .replace('T', ' ')
        .replace(/\.\d+/, '')
        .replace(/(\+\d{2}:\d{2}|Z)$/, '');
    },

    units() {
      return this.activeTab === 'housing' ? this.grid.housing : this.grid.parking;
    },

    groupField() {
      return this.activeTab === 'housing' ? 'floor' : 'basement_level';
    },

    // 分組/排序邏輯比照 admin HousingHeatmap（房屋：樓層數字遞減）
    // 與 ParkingHeatmap（車位：地下層 B1→B4 遞增），資料全由 store grid 提供，不呼叫 API。
    gridRows() {
      const isHousing = this.activeTab === 'housing';
      const groupField = this.groupField;
      const groupMap = new Map();

      this.units.forEach(function(u) {
        const code = u.unit_code;
        const dash = code.indexOf('-');
        let group, unit;
        if (dash === -1) {
          group = code;
          unit = code;
        } else {
          group = u[groupField] || code.slice(0, dash);
          unit = code.slice(dash + 1);
        }
        const cell = Object.assign({}, u, { unit: unit });
        if (!groupMap.has(group)) groupMap.set(group, []);
        groupMap.get(group).push(cell);
      });

      const groups = Array.from(groupMap.keys());
      if (isHousing) {
        // 樓層數字遞減（頂樓在前），非數字置後
        groups.sort(function(a, b) {
          const na = parseInt(a, 10);
          const nb = parseInt(b, 10);
          const aIsNaN = isNaN(na);
          const bIsNaN = isNaN(nb);
          if (aIsNaN && bIsNaN) return a.localeCompare(b);
          if (aIsNaN) return 1;
          if (bIsNaN) return -1;
          return nb - na;
        });
      } else {
        // 地下層 B1→B4 遞增，非數字置後
        groups.sort(function(a, b) {
          const na = parseInt(String(a).replace(/\D/g, ''), 10);
          const nb = parseInt(String(b).replace(/\D/g, ''), 10);
          const aIsNaN = isNaN(na);
          const bIsNaN = isNaN(nb);
          if (aIsNaN && bIsNaN) return String(a).localeCompare(String(b));
          if (aIsNaN) return 1;
          if (bIsNaN) return -1;
          return na - nb;
        });
      }

      return groups.map(function(group) {
        const cells = groupMap.get(group).slice().sort(function(a, b) {
          if (isHousing) {
            const re = /([A-Za-z]+)(\d+)/;
            const ma = re.exec(a.unit);
            const mb = re.exec(b.unit);
            if (ma && mb) {
              const cmp = ma[1].localeCompare(mb[1]);
              if (cmp !== 0) return cmp;
              return parseInt(ma[2], 10) - parseInt(mb[2], 10);
            }
            return a.unit.localeCompare(b.unit);
          }
          const da = /^\d+$/.test(a.unit);
          const db = /^\d+$/.test(b.unit);
          if (da && db) return parseInt(a.unit, 10) - parseInt(b.unit, 10);
          return String(a.unit).localeCompare(String(b.unit));
        });
        return { floor: group, cells: cells };
      });
    },
  },

  methods: {
    ...mapMutations('urbanRenewalSelect', ['REMOVE_HOUSING', 'REMOVE_PARKING']),

    isOwnSelected(cell) {
      if (!cell) return false;
      const list = this.activeTab === 'housing' ? this.selectedHousing : this.selectedParking;
      return list.some(u => u.id === cell.id);
    },

    effectiveCount(cell) {
      // 組數只顯示後台統計，不加本次模擬試算的自選（客戶：試算階段 +1 會誤導）。
      // 自己有選仍以打勾（is-selected / already-added）呈現，見 cellClass / UnitDialog。
      if (!cell) return 0;
      return (cell.selection_count || 0);
    },

    cellClass(cell) {
      // 非優先區橘色標記暫時註解（客戶：標記會被人針對性挑選）；非優先格改回依組數正常上色。
      // 點擊警告仍保留於 addFromDialog（NonPriorityDialog）與 UnitDialog。
      // const nonPriority = this.activeTab === 'housing' && cell.is_priority === false;
      const c = this.effectiveCount(cell);
      return {
        // 'ur-sel-grid-cell-nonpriority': nonPriority,
        'ur-sel-grid-cell-one': c === 1,
        'ur-sel-grid-cell-multi': c >= 2,
        'is-selected': this.isOwnSelected(cell),
      };
    },

    openUnit(cell, e) {
      // 點同一格再點一次 → 收合（設計稿 toggle 行為）
      if (this.dialogUnit && this.dialogUnit.id === cell.id && this.dialogType === this.activeTab) {
        this.closeUnit();
        return;
      }
      this.anchorRect = e.currentTarget.getBoundingClientRect();
      this.dialogUnit = cell;
      this.dialogType = this.activeTab;
    },

    closeUnit() {
      this.dialogUnit = null;
      this.anchorRect = null;
    },

    addFromDialog(cell) {
      // FE-P08：非優先房屋先攔截確認/重選，暫存待確認單元
      if (this.dialogType === 'housing' && cell.is_priority === false) {
        this.pendingUnit = cell;
        this.closeUnit();
        this.nonPriorityVisible = true;
        return;
      }
      // FE-P06：新增前 110% 硬擋，統一交由 store action 守門
      this.$store.dispatch('urbanRenewalSelect/addUnit', { type: this.dialogType, unit: cell });
      this.closeUnit();   // 超選時 store 會開 OverSelectDialog；未超選則已加入
    },

    removeFromDialog(cell) {
      // 已選取的格子 → 於 popover 取消此選配
      if (this.dialogType === 'housing') this.REMOVE_HOUSING(cell.id); else this.REMOVE_PARKING(cell.id);
      this.closeUnit();
    },

    confirmNonPriority() {
      // 確認後仍需經 addUnit 的 110% 守門（超選時 store 會開 OverSelectDialog）
      if (this.pendingUnit) {
        this.$store.dispatch('urbanRenewalSelect/addUnit', { type: 'housing', unit: this.pendingUnit });
      }
      this.nonPriorityVisible = false;
      this.pendingUnit = null;
    },

    cancelNonPriority() {
      this.nonPriorityVisible = false;
      this.pendingUnit = null;
    },
  },
};
</script>
